import Checkbox from '@/Components/Checkbox';
import InputError from '@/Components/InputError';
import InputLabel from '@/Components/InputLabel';
import PrimaryButton from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import ValidatedTextInput from '@/Components/ValidatedTextInput';
import ValidationMessage from '@/Components/ValidationMessage';
import ApplicationTitle from '@/Components/ApplicationTitle';
import CorporateLogo from '@/Components/CorporateLogo';
import GuestLayout from '@/Layouts/GuestLayout';
import { Head, Link, useForm } from '@inertiajs/react';
import { useState, useEffect, useCallback } from 'react';
import { useFieldValidation } from '@/hooks/useFieldValidation';

export default function Register() {
    // Modo nuevo: código manual opcional de academia.
    const [academyCode, setAcademyCode] = useState('');
    const [isIndependent, setIsIndependent] = useState(true); // por defecto libre
    const [academyCheck, setAcademyCheck] = useState({ status: 'idle', valid: false, academy: null, message: '' });
    const [pendingValidation, setPendingValidation] = useState(false);

    const validateAcademyCode = useCallback(async (code) => {
        if (!code) {
            setAcademyCheck({ status: 'idle', valid: false, academy: null, message: '' });
            return;
        }
        const currentToken = code; // simple token basado en el valor
        setPendingValidation(true);
        setAcademyCheck(prev => ({ ...prev, status: 'checking', token: currentToken }));
        try {
            const resp = await fetch(route('academy.validate-code', { code }));
            const json = await resp.json();
            // Evitar sobrescribir si el usuario ya cambió el código
            setAcademyCheck(prev => {
                if (prev.token && prev.token !== currentToken) return prev; // respuesta obsoleta
                if (json.valid) {
                    return { status: 'valid', valid: true, academy: json.academy, message: '', token: currentToken };
                }
                return { status: 'invalid', valid: false, academy: null, message: json.message || 'Código no válido', token: currentToken };
            });
        } catch (e) {
            setAcademyCheck(prev => ({ ...prev, status: 'error', valid: false, academy: null, message: 'Error validando código' }));
        } finally {
            setPendingValidation(false);
        }
    }, []);

    // Hook de validación en tiempo real
    const {
        validateEmail,
        validatePhone,
        getFieldState,
        isFormValid,
        cleanup
    } = useFieldValidation();

    const { data, setData, post, processing, errors, reset } = useForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        phone: '',
        academy_code: '', // nuevo campo (backend resolverá id si existe)
        terms: false,
    });

    // Limpiar timeouts al desmontar el componente
    useEffect(() => {
        return () => cleanup();
    }, [cleanup]);

    const submit = (e) => {
        e.preventDefault();
        // No volver a disparar validaciones aquí para evitar comprobaciones extra tras registrar
        // Solo comprobamos el estado actual del formulario
        if (!isFormValid()) return;
        // Verificar cliente: contraseñas coinciden y condiciones generales
        if (!canSubmit()) return;

        // Si va por libre, academy_id debe ser null
        const formData = {
            ...data,
            academy_code: academyCode.trim() === '' ? null : academyCode.trim().toUpperCase(),
        };

        // Limpiar cualquier validación pendiente antes de enviar
        cleanup();

        post(route('register'), {
            data: formData,
            onFinish: () => reset('password', 'password_confirmation'),
        });
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        setData('email', email);
        validateEmail(email);
    };

    const handlePhoneChange = (e) => {
        const phone = e.target.value;
        setData('phone', phone);
        validatePhone(phone);
    };

    const handleIndependentChange = (checked) => {
        setIsIndependent(checked);
        if (checked) {
            // Modo libre: limpiar código y validaciones
            setAcademyCode('');
            setData('academy_code', '');
            setAcademyCheck({ status: 'idle', valid: false, academy: null, message: '' });
        } else {
            // Al activar modo academia, resetear estado previo de validación
            setAcademyCheck({ status: 'idle', valid: false, academy: null, message: '' });
        }
    };

    // Debounce validation
    useEffect(() => {
        if (isIndependent) return; // No validar en modo libre
        const code = academyCode.trim().toUpperCase();
        if (code === '') {
            setAcademyCheck({ status: 'idle', valid: false, academy: null, message: '' });
            return;
        }
        const t = setTimeout(() => validateAcademyCode(code), 400);
        return () => clearTimeout(t);
    }, [academyCode, isIndependent, validateAcademyCode]);

    const canSubmit = () => {
        if (!isFormValid()) return false;
        // Ensure passwords match on client side
        if (data.password !== data.password_confirmation) return false;
        if (isIndependent) return true;
        if (academyCode.trim() === '') return false; // requiere código válido si no es independiente
        return academyCheck.valid;
    };

    // Adaptar el estado de validación al formato usado por ValidatedTextInput / ValidationMessage
    const getAcademyValidationState = () => {
        if (isIndependent || academyCode.trim() === '') return { status: 'idle', message: '' };
        switch (academyCheck.status) {
            case 'checking':
                return { status: 'checking', message: 'Validando código...' };
            case 'valid':
                return { status: 'valid', message: academyCheck.academy ? `Academia: ${academyCheck.academy.name}` : 'Código válido' };
            case 'invalid':
                return { status: 'invalid', message: academyCheck.message || 'Código no válido' };
            case 'error':
                return { status: 'invalid', message: 'Error al validar. Intenta de nuevo.' };
            default:
                return { status: 'idle', message: '' };
        }
    };

    return (
        <GuestLayout>
            <Head title="Registro - ValidaTest" />

            {/* Hero Section */}
            <div className="mb-8 text-center">
                <div className="flex justify-center mb-4">
                    <ApplicationTitle transparent={false} style={{ maxHeight: '60px' }} />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Crea tu cuenta y comienza a preparar tus oposiciones</p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
                <form onSubmit={submit} className="space-y-6">
                    {/* Información Personal */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                            Información Personal
                        </h3>

                        <div>
                            <InputLabel
                                htmlFor="name"
                                value="Nombre completo"
                                className="text-gray-700 dark:text-gray-300 font-medium"
                            />
                            <TextInput
                                id="name"
                                name="name"
                                value={data.name}
                                className="mt-2 block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                autoComplete="name"
                                isFocused={true}
                                onChange={(e) => setData('name', e.target.value)}
                                placeholder="Tu nombre completo"
                                required
                            />
                            <InputError message={errors.name} className="mt-2" />
                        </div>

                        <div>
                            <InputLabel
                                htmlFor="email"
                                value="Correo electrónico"
                                className="text-gray-700 dark:text-gray-300 font-medium"
                            />
                            <ValidatedTextInput
                                id="email"
                                type="email"
                                name="email"
                                value={data.email}
                                className="mt-2 block w-full px-4 py-3 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 transition-colors"
                                autoComplete="username"
                                onChange={handleEmailChange}
                                placeholder="tu.email@ejemplo.com"
                                validationState={getFieldState('email')}
                                required
                            />
                            <ValidationMessage validationState={getFieldState('email')} />
                            <InputError message={errors.email} className="mt-2" />
                        </div>

                        <div>
                            <InputLabel
                                htmlFor="phone"
                                value="Teléfono (opcional)"
                                className="text-gray-700 dark:text-gray-300 font-medium"
                            />
                            <ValidatedTextInput
                                id="phone"
                                type="tel"
                                name="phone"
                                value={data.phone}
                                className="mt-2 block w-full px-4 py-3 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 transition-colors"
                                autoComplete="tel"
                                onChange={(e) => {
                                    // Solo dígitos y máximo 15
                                    const digitsOnly = e.target.value.replace(/\D+/g, '').slice(0, 15);
                                    e.target.value = digitsOnly;
                                    handlePhoneChange(e);
                                }}
                                maxLength={15}
                                placeholder="123 456 789"
                                validationState={getFieldState('phone')}
                            />
                            <ValidationMessage validationState={getFieldState('phone')} />
                            <InputError message={errors.phone} className="mt-2" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <InputLabel
                                    htmlFor="password"
                                    value="Contraseña"
                                    className="text-gray-700 dark:text-gray-300 font-medium"
                                />
                                <TextInput
                                    id="password"
                                    type="password"
                                    name="password"
                                    value={data.password}
                                    className="mt-2 block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    autoComplete="new-password"
                                    onChange={(e) => setData('password', e.target.value)}
                                    placeholder="••••••••"
                                    required
                                />
                                <InputError message={errors.password} className="mt-2" />
                            </div>

                            <div>
                                <InputLabel
                                    htmlFor="password_confirmation"
                                    value="Confirmar contraseña"
                                    className="text-gray-700 dark:text-gray-300 font-medium"
                                />
                                <TextInput
                                    id="password_confirmation"
                                    type="password"
                                    name="password_confirmation"
                                    value={data.password_confirmation}
                                    className="mt-2 block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    autoComplete="new-password"
                                    onChange={(e) => setData('password_confirmation', e.target.value)}
                                    placeholder="••••••••"
                                    required
                                />
                                <InputError message={
                                    errors.password_confirmation || (data.password !== data.password_confirmation ? 'Las contraseñas no coinciden.' : null)
                                } className="mt-2" />
                            </div>
                            
                            <div className="col-span-1 md:col-span-2 text-black dark:text-gray-300 text-sm">
                                <ol>
                                    Requisitos de la contraseña:
                                    
                                <li>• Al menos 8 caracteres</li>
                                
                                <li>• Incluye al menos una mayúscula</li>
                                
                                <li>• Incluye al menos una minúscula</li>
                                
                                <li>• Incluye al menos un número</li>
                                
                                <li>• Incluye al menos un símbolo</li>

                                </ol>
                                
                            </div>
                        </div>
                    </div>

                    {/* Código de Academia Opcional con validación */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                            Academia (opcional)
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Si tienes un código de academia, introdúcelo. Déjalo vacío para estudiar por libre.
                        </p>
                        <div>
                            <InputLabel htmlFor="academy_code" value="Código de academia" className="text-gray-700 dark:text-gray-300 font-medium" />
                            <ValidatedTextInput
                                id="academy_code"
                                name="academy_code"
                                value={academyCode}
                                disabled={isIndependent}
                                className="mt-2 block w-full px-4 py-3 dark:bg-gray-700 dark:text-gray-100 rounded-lg shadow-sm focus:ring-2 transition-colors disabled:opacity-50"
                                placeholder={isIndependent ? 'Modo libre activo' : 'Ej: ABC123'}
                                autoComplete="off"
                                onChange={(e) => {
                                    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
                                    e.target.value = code;
                                    setAcademyCode(code);
                                    setData('academy_code', code);
                                }}
                                validationState={getAcademyValidationState()}
                            />
                            <ValidationMessage validationState={getAcademyValidationState()} />
                            <InputError message={errors.academy_code || errors.academy_id} className="mt-2" />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                name="independent_mode"
                                checked={isIndependent}
                                onChange={(e) => handleIndependentChange(e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500 mt-0.5"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Estudiar por libre (sin academia)</span>
                        </div>
                    </div>

                    {/* Términos y Condiciones */}
                    <div className="flex items-start">
                        <Checkbox
                            name="terms"
                            checked={data.terms}
                            onChange={(e) => setData('terms', e.target.checked)}
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500 mt-0.5"
                            required
                        />
                        <div className="ml-3">
                            <label className="text-sm text-gray-600 dark:text-gray-400">
                                Acepto los{' '}
                                <Link
                                    href={route('terms')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 font-medium underline"
                                >
                                    términos y condiciones
                                </Link>{' '}
                                y la{' '}
                                <Link
                                    href={route('privacy')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 font-medium underline"
                                >
                                    política de privacidad
                                </Link>
                            </label>
                            <InputError message={errors.terms} className="mt-1" />
                        </div>
                    </div>

                    <PrimaryButton
                        className="w-full bg-gradient-to-r flex text-center items-center justify-center from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-3 px-4 rounded-lg shadow-lg transition duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        disabled={processing || !canSubmit() || pendingValidation}
                    >
                        {processing ? (
                            <div className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creando cuenta...
                            </div>
                        ) : !canSubmit() ? (
                            'Verificar campos requeridos'
                        ) : (
                            'Crear Cuenta'
                        )}
                    </PrimaryButton>
                </form>

                {/* Divider */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                        ¿Ya tienes una cuenta?{' '}
                        <Link
                            href={route('login')}
                            className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                        >
                            Inicia sesión aquí
                        </Link>
                    </p>
                </div>

            </div>
        </GuestLayout>
    );
}
